/**
 * Call missions for the CSR's screens: the missions list, a mission's progress, the next customer (a claim),
 * the call's outcome, and the outcome taxonomy. Every endpoint is under `/call-missions`
 * (`backend/app/api/endpoints/call_missions.py`).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addMinutes, isAfter, secondsToMilliseconds } from "date-fns";

import type { OutcomeOption, OutcomeTemplate } from "@/components/calls/call-outcomes";
import type { ScriptObjection } from "@/components/calls/script";
import type { AudienceCriteria } from "@/hooks/use-campaign-audiences";
import { fetchApi } from "@/lib/api";

export type MissionGoalMetric = "booked_jobs" | "surveys_booked" | "renewals" | "payments" | "reached";

export interface MissionGoal {
  metric: MissionGoalMetric;
  target: number;
  by?: string | null;
}

export interface MissionProgress {
  targets: number;
  worked: number;
  done: number;
  /** Customers with an outcome that counts toward the goal: the team's, then the viewer's own. */
  goal: number;
  mine_goal: number;
  /** The viewer's calls today on this mission. */
  today: { calls: number; reached: number; goal: number };
  /** This business week's best day for the goal, team-wide; null with no goal-counted attempts yet. */
  best_day: { date: string; count: number } | null;
}

export interface MissionDue {
  /** Customers the viewer can call now. */
  now: number;
  /** Callbacks the viewer promised that are due now. */
  callbacks: number;
  /** The viewer's next callback still to come (ISO), or null. */
  next_callback_at: string | null;
}

export interface MissionSummary {
  id: string;
  name: string;
  status: "active" | "paused" | "draft";
  tenant_ids: string[];
  brief: string | null;
  /** The goal's date, else the mission's end date (yyyy-MM-dd). */
  ends: string | null;
  goal: MissionGoal | null;
  outcome_template: string;
  audience_name: string | null;
  audience_id: string | null;
  source: string;
  st_campaign_id: string | null;
  /** Where it was started from, and the ServiceTitan business unit and job type a booking takes. */
  mission: {
    started_from?: string | null;
    /** The gallery template it was launched from, by key. */
    template?: string | null;
    st_business_unit_id?: string | null;
    st_job_type_id?: string | null;
  };
  /** The flat icon badge: its gallery template's icon and color, else its kind's. */
  badge?: MissionBadgeKey;
  created_at: string | null;
  /** Who set the mission up, for the hero's Set By pill and the mission page's chip; null when unknown. */
  set_by?: { user_id: string; name: string; email: string | null } | null;
  in_pool: boolean;
  /** One mission's read only: whether the viewer may change it (a missions manager holding every brand on it). */
  can_manage?: boolean;
  pool: { user_id: string; name: string | null }[];
  /** A CSR who holds one of its brands may join its pool themselves, from All Missions. */
  open_to_team: boolean;
  /** The viewer is in the pool because they joined it themselves (All Missions' Join), not a manager's
   *  assignment: only then does My Missions offer them Leave. */
  self_joined: boolean;
  progress: MissionProgress;
  /** Only for a mission the viewer calls for, while it is on. */
  due: MissionDue | null;
  /** The scheduler's latest refresh of a standing mission's list (ISO time, rows added); null when none. */
  last_refresh?: { at: string; added: number } | null;
}

export interface MissionTarget {
  run_id: string;
  drip_id: string;
  tenant_id: string;
  source: string;
  source_id: string;
  pipedrive_deal_id: string | null;
  /** A canonical person id (Customer 360) when the audience resolved one, else a ServiceTitan key. */
  customer_key: string | null;
  customer: { name?: string | null; phone?: string | null } | null;
  record: Record<string, unknown> | null;
  touch: number;
  /** "phone": a call to make. "text": a text to send from the CSR's own RingCentral line (`text`). */
  channel: "phone" | "text";
  step: { title: string | null; script: string };
  /** A text step: its words, whether it is the renewal pay link, and why it can't go now. Null on a call. */
  text: MissionText | null;
  phone: {
    tries?: number;
    outcome?: string;
    note?: string | null;
    attempts?: { at: string; user_id: string; reach: string; outcome: string; note?: string | null }[];
  };
  claimed_at: string | null;
  claim_expires_at: string | null;
  outcome_template: string;
  /** Kory's one line on why we are calling, or null until it is written. A null `line` shows no line. */
  why: { line: string | null; at: string | null; model: string | null } | null;
  /** A renewal's invoice, signed pay link and the text with it; null off a renewal. */
  collect: MissionCollect | null;
  /** Every number on file for the customer (E.164), the mission's own first. */
  numbers?: string[];
}

export interface MissionText {
  body: string | null;
  pay_link: boolean;
  block: { code: string; message: string; opens_at?: string } | null;
}

export interface MissionCollect {
  st_invoice_id: string | null;
  tenant_id: string;
  /** Null when the invoice cannot be paid through the portal; `reason` says why. */
  pay_url: string | null;
  pay_text: string | null;
  sandbox: boolean;
  reason: string | null;
}

export interface BookedJob {
  job_id: string;
  job_number: string | null;
  created_on: string | null;
  campaign_id: string | null;
  campaign_expected: string | null;
  /** False when the job is not on the mission's ServiceTitan campaign; null when the mission names none. */
  campaign_matches: boolean | null;
  status: string | null;
  source: string | null;
  /** A membership visit ServiceTitan marked Won with this job. */
  membership_visit?: boolean;
  note_writeback_id: string | null;
  /** Who found the job first: the call screen's check after Book in ServiceTitan, or the I Booked It tap. */
  detected_by?: "poll" | "tap";
}

/** The call screen's check after Book in ServiceTitan: the job found in the mirror, not yet logged. */
export interface BookedJobCheck {
  found: boolean;
  job: BookedJob | null;
  window_days?: number;
  test?: true;
}

export interface BookedResult {
  found: boolean;
  job: BookedJob | null;
  outcome: OutcomeResult | null;
  /** A job booked within this many days of the call counts. */
  window_days: number;
  /** Booked Sales Survey found only a job ServiceTitan does not class as a sales survey: nothing was logged. */
  not_survey?: { job_number: string; job_type: string | null };
  /** After a Booked Sales Survey: how many salespeople were told, or why nobody was. */
  survey_alert?: { notified: number; reason: "no_tech" | "no_login" | "error" | null; names: string[] } | null;
}

export interface HandToSalesResult {
  route: "todo" | "reopen_signal" | "manual_lead";
  deal_id: string | null;
  todo_id: string | null;
  manual_lead_id: string | null;
}

/** A mission save made in personal test mode: nothing was written and the claim ended, so the next ask moves on. */
export interface TestModeSave {
  run_id: string;
  test: true;
  released: boolean;
}

export function savedInTestMode(out: object): out is TestModeSave {
  return "test" in out && out.test === true;
}

export interface OutcomeIn {
  reach: "reached" | "not_reached";
  outcome: string;
  note?: string | null;
  callback_at?: string | null;
  rc_session_id?: string | null;
  number?: string | null;
  /** The call rang the tester's own number: the server saves nothing. */
  test_call?: boolean;
}

export interface OutcomeResult {
  run_id: string;
  touch: number;
  done: boolean;
  ended: boolean;
  contact_id: string | null;
  deal_id: string | null;
}

export interface DaysToOffer {
  /** Always true until the open-slots Business Definition is ratified. */
  estimate: boolean;
  /** Last month's completed jobs per weekday. */
  pace: number;
  /** The month the pace is from (yyyy-MM-01). */
  basis: string | null;
  days: { date: string; booked: number; open: number }[];
}

interface OutcomeTemplates {
  default: string;
  templates: Record<string, OutcomeTemplate & { name: string }>;
}

export const MISSIONS_KEY = ["call-missions"] as const;

/** The live missions, and how many have ended (`past_count`); the ended ones load with `usePastMissions`. */
export function useMissions() {
  return useQuery<{ missions: MissionSummary[]; past_count: number }>({
    queryKey: [...MISSIONS_KEY, "list"],
    queryFn: () => fetchApi("/call-missions"),
    staleTime: 30_000,
  });
}

/** The missions past their end, read only once the viewer opens Past Missions. */
export function usePastMissions(enabled: boolean) {
  return useQuery<{ missions: MissionSummary[] }>({
    queryKey: [...MISSIONS_KEY, "list", "past"],
    queryFn: () => fetchApi("/call-missions?past=true"),
    enabled,
    staleTime: 30_000,
  });
}

/** A mission as Up Next and the patch gallery name it. */
export interface MissionRef {
  id: string;
  name: string;
  tenant_ids: string[];
  template: string | null;
  outcome_template: string;
  badge: MissionBadgeKey;
  /** Always on (one patch per monthly tour), else a push with an end date (one patch). */
  standing: boolean;
  ends: string | null;
}

export interface UpNextRow {
  run_id: string;
  tenant_id: string;
  name: string | null;
  /** Kory's one line on why we are calling; null until it is written. */
  why: string | null;
  /** When the CSR promised to call back; null when it is not their callback. */
  callback_at: string | null;
  mission: MissionRef;
}

/** Up Next in My Calls: the next customers across the CSR's missions, read-only. Empty outside calling hours. */
export function useUpNext(enabled: boolean, limit = 5) {
  return useQuery<{ up_next: UpNextRow[] }>({
    queryKey: [...MISSIONS_KEY, "up-next", limit],
    queryFn: () => fetchApi(`/call-missions/up-next?limit=${limit}`),
    enabled,
    staleTime: 30_000,
  });
}

export interface EarnedPatchRow {
  /** The month earned, `yyyy-MM`. */
  month: string;
  count: number;
  foil: boolean;
}

/** One mission's shelf in the patch gallery. */
export interface PatchShelf {
  mission: MissionRef;
  target: number;
  goal: number;
  patches: EarnedPatchRow[];
  tours: number;
  rank: 0 | 1 | 2 | 3;
  /** The next rank's tour count (standing), or the goal's target (a push not yet earned); null at the top rank. */
  next_at: number | null;
  /** Tours so far (standing), or customers counted toward the goal (push). */
  have: number;
}

/** The CSR's patch gallery: the missions they call for, live or past. */
export function useMissionPatches(enabled: boolean) {
  return useQuery<{ shelves: PatchShelf[]; rank_tours: number[] }>({
    queryKey: [...MISSIONS_KEY, "patches"],
    queryFn: () => fetchApi("/call-missions/patches"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useMission(dripId: string | undefined) {
  return useQuery<MissionSummary>({
    queryKey: [...MISSIONS_KEY, "mission", dripId],
    queryFn: () => fetchApi(`/call-missions/${dripId}`),
    enabled: !!dripId,
  });
}

export function useDaysToOffer(dripId: string | undefined) {
  return useQuery<DaysToOffer>({
    queryKey: [...MISSIONS_KEY, "days", dripId],
    queryFn: () => fetchApi(`/call-missions/${dripId}/days`),
    enabled: !!dripId,
    staleTime: 5 * 60_000,
  });
}

export interface NextPreview {
  /** The next customer `claim_next` would most likely hand the CSR; null when nobody else is due or the preview
   * can't say who. A preview, never a claim: it never reserves this customer. */
  next: { name: string | null; tenant_id: string } | null;
}

/** The Up Next bar's named preview, beside the mission's own `due` count; never claims. */
export function useNextPreview(dripId: string | undefined, excludeRunId: string | undefined, enabled: boolean) {
  return useQuery<NextPreview>({
    queryKey: [...MISSIONS_KEY, "next-preview", dripId, excludeRunId],
    queryFn: () =>
      fetchApi(
        `/call-missions/${dripId}/next-preview${excludeRunId ? `?exclude_run_id=${encodeURIComponent(excludeRunId)}` : ""}`,
      ),
    enabled: !!dripId && enabled,
    staleTime: 15_000,
  });
}

/** A ServiceTitan business unit, job type or campaign: its id, and its name when the mirror has it. */
export interface StRef {
  id: string;
  name: string | null;
}

/** The Business Definition `@member_status`: is the customer a member today. */
export type MemberStatus = "Member" | "NonMember";

/** A membership visit the customer is owed and nobody has booked, and what it books as in ServiceTitan. */
export interface VisitDue {
  name: string | null;
  /** yyyy-MM-dd */
  due_date: string;
  due_state: "overdue" | "upcoming";
  business_unit: StRef | null;
  job_type: StRef | null;
  campaign: StRef | null;
}

/** Where Book in ServiceTitan opens, and what to book there, from the ServiceTitan mirror. */
export interface RunServiceTitan {
  tenant_id: string;
  st_customer_id: string | null;
  st_location_id: string | null;
  /** Null when the customer has no ServiceTitan record in the run's brand family. */
  member_status: MemberStatus | null;
  visits_due: VisitDue[];
  /** The campaign, business unit and job type the mission's manager set. */
  mission: { campaign: StRef | null; business_unit: StRef | null; job_type: StRef | null };
}

export function useRunServiceTitan(runId: string | undefined) {
  return useQuery<RunServiceTitan>({
    queryKey: [...MISSIONS_KEY, "run-st", runId],
    queryFn: () => fetchApi(`/call-missions/runs/${runId}/servicetitan`),
    enabled: !!runId,
    // Membership and visits change (a visit gets booked in ServiceTitan), so a callback to this run reads them again.
    staleTime: 60_000,
  });
}

/** A badge's icon and color, by key (`call_mission_templates.py`). */
export interface MissionBadgeKey {
  icon: string;
  color: string;
}

export type MissionTeam = "service" | "memberships" | "sales" | "collections" | "operations";

/** A gallery template (`backend/app/services/call_mission_templates.py`); a ready one carries its list as of today. */
export interface MissionTemplate extends MissionBadgeKey {
  key: string;
  name: string;
  team: MissionTeam;
  kind: "standing" | "push";
  who: string;
  /** The page whose Start Mission button launches this list, by its nav title: the card's "From …" line. Null while
   * no page starts it. */
  from_page: string | null;
  purpose: string;
  goal: string;
  includes: string[];
  excludes: string[];
  ready: boolean;
  /** Coming soon: the one piece today's lists lack. */
  missing: string | null;
  /** A filter the manager sets on the list before saving it. */
  choose_at_launch: string | null;
  outcome_template: string | null;
  criteria: AudienceCriteria | null;
  steps: { title: string; day: number }[];
  /** The script the first call starts from (Hello / Why / Book It) and its "If They Say" list; null while coming soon.
   * Optional during deploys. */
  script?: { brief: string; objections: ScriptObjection[] } | null;
}

export interface MissionTemplateGallery {
  teams: { key: MissionTeam; name: string; description: string }[];
  templates: MissionTemplate[];
}

/** The template gallery. Managers only, like New Mission. */
export function useMissionTemplates(enabled = true) {
  return useQuery<MissionTemplateGallery>({
    queryKey: [...MISSIONS_KEY, "templates"],
    queryFn: () => fetchApi("/call-missions/templates"),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Every outcome template, from the backend taxonomy (`app/services/call_outcomes.py`). */
export function useOutcomeTemplates() {
  return useQuery<OutcomeTemplates>({
    queryKey: [...MISSIONS_KEY, "outcomes"],
    queryFn: () => fetchApi("/call-missions/outcomes"),
    staleTime: Infinity,
  });
}

/**
 * One template's options, or undefined while it loads. `withoutDnc` drops Do Not Call, for a surface whose save
 * writes no suppression (the to-do drawer's call): a confirm that says no one will call again must be true.
 */
export function useOutcomeTemplate(name: string | undefined, { withoutDnc = false } = {}): OutcomeTemplate | undefined {
  const { data } = useOutcomeTemplates();
  if (!data || !name) return undefined;
  const t = data.templates[name] ?? data.templates[data.default];
  if (!t) return undefined;
  const keep = (o: OutcomeOption) => !(withoutDnc && o.dnc);
  return { reached: t.reached.filter(keep), not_reached: t.not_reached.filter(keep) };
}

/** Claim the next customer to call; asking again renews the viewer's own claim. */
/** `beforeCall`: the countdown's ask just before it calls, which also rechecks "Contacted recently" on the CSR's own claim. */
export function claimNext(dripId: string, { beforeCall = false } = {}): Promise<{ target: MissionTarget | null }> {
  return fetchApi(`/call-missions/${dripId}/next${beforeCall ? "?before_call=true" : ""}`, { method: "POST" });
}

export function releaseTarget(runId: string): Promise<{ run_id: string; released: boolean }> {
  return fetchApi(`/call-missions/runs/${runId}/release`, { method: "POST" });
}

/** Skip the customer: someone else comes next, and they come back to this CSR only when nobody else is left. */
export function skipTarget(runId: string): Promise<{ run_id: string; skipped: boolean }> {
  return fetchApi(`/call-missions/runs/${runId}/skip`, { method: "POST" });
}

/** Pass the customer's text without sending it: they go on to the mission's next step. */
export function skipText(runId: string): Promise<{ run_id: string; skipped: boolean }> {
  return fetchApi(`/call-missions/runs/${runId}/skip-text`, { method: "POST" });
}

export function useLogOutcome(dripId: string) {
  const qc = useQueryClient();
  return useMutation<OutcomeResult | TestModeSave, Error, { runId: string; body: OutcomeIn }>({
    mutationFn: ({ runId, body }) =>
      fetchApi(`/call-missions/runs/${runId}/outcome`, { method: "POST", body: JSON.stringify(body) }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "mission", dripId] });
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "list"] });
      // A logged outcome can move who's up next (the customer is done) and earn a patch (goal hit).
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "up-next"] });
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "patches"] });
    },
  });
}

/** "I Booked It": the server looks for the customer's new ServiceTitan job and logs the booking only when it finds it. */
export function useConfirmBooked(dripId: string) {
  const qc = useQueryClient();
  return useMutation<
    BookedResult | TestModeSave,
    Error,
    {
      runId: string;
      body: {
        outcome?: string;
        /** The job the call screen found: only that job is logged. */
        job_id?: string | null;
        note?: string | null;
        rc_session_id?: string | null;
        number?: string | null;
        test_call?: boolean;
      };
    }
  >({
    mutationFn: ({ runId, body }) =>
      fetchApi(`/call-missions/runs/${runId}/booked`, { method: "POST", body: JSON.stringify(body) }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "mission", dripId] });
      // A booked confirmation can move who's up next and earn a patch (goal hit), same as logging an outcome.
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "up-next"] });
      void qc.invalidateQueries({ queryKey: [...MISSIONS_KEY, "patches"] });
    },
  });
}

/** Keep when the CSR clicked Book in ServiceTitan, to measure the link-out. */
export function recordBookClick(runId: string): Promise<unknown> {
  return fetchApi(`/call-missions/runs/${runId}/book-click`, { method: "POST" });
}

// After Book in ServiceTitan the call screen checks the mirror this often, for at most this long.
export const BOOKED_CHECK_INTERVAL_MS = secondsToMilliseconds(20);
export const BOOKED_CHECK_FOR_MINUTES = 30;

/** When the check that started at `since` stops looking. */
export function bookedCheckEnds(since: Date): Date {
  return addMinutes(since, BOOKED_CHECK_FOR_MINUTES);
}

/**
 * After Book in ServiceTitan: look for the job in the mirror every `BOOKED_CHECK_INTERVAL_MS` while `since` is set,
 * the tab is visible and nothing is found, until `bookedCheckEnds(since)` or `stopped`. A found job stays in the
 * result after the check stops. Logs nothing: the CSR confirms.
 */
export function useBookedJobCheck(
  runId: string | undefined,
  since: Date | null,
  stopped: boolean,
  rcSessionId: () => string | null,
) {
  return useQuery<BookedJobCheck>({
    queryKey: [...MISSIONS_KEY, "booked-check", runId, since?.toISOString() ?? null],
    queryFn: () =>
      fetchApi(`/call-missions/runs/${runId}/booked-job/check`, {
        method: "POST",
        body: JSON.stringify({ rc_session_id: rcSessionId() }),
      }),
    enabled: !!runId && since !== null && !stopped,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.found || query.state.data?.test || since === null || stopped || isAfter(new Date(), bookedCheckEnds(since))
        ? false
        : BOOKED_CHECK_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/** Hand the customer to sales after a Passed to Sales call; `rep_user_id` when the server asks for the rep. */
export function handToSales(runId: string, body: { note?: string | null; rep_user_id?: string | null }) {
  return fetchApi(`/call-missions/runs/${runId}/hand-to-sales`, {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<HandToSalesResult | TestModeSave>;
}

/** A renewal call's collect actions: the technician collects on the visit, or a promised payment day. */
export function collectOnCall(
  runId: string,
  body:
    | { action: "tech_collects"; note?: string | null; test_call?: boolean }
    | {
        action: "promised_to_pay";
        promised_by: string;
        note?: string | null;
        rc_session_id?: string | null;
        number?: string | null;
        test_call?: boolean;
      },
) {
  return fetchApi(`/call-missions/runs/${runId}/collect`, { method: "POST", body: JSON.stringify(body) }) as Promise<
    | {
        action: string;
        job_id: string | null;
        note_writeback_id: string | null;
        outcome: OutcomeResult | null;
      }
    | TestModeSave
  >;
}

// ---------------------------------------------------------------------------
// The manager's side: the mission page, its Board, and the New Mission wizard's ServiceTitan settings
// ---------------------------------------------------------------------------

export interface MissionSteps {
  targets: number;
  /** Through every step. */
  finished: number;
  /** Out of the mission (Do Not Call, a payment, removed). */
  stopped: number;
  steps: {
    n: number;
    title: string;
    /** "phone", "text" (`kind` "pay_link" for the renewal pay link) or "email" (the renewal invoice email's day). */
    channel: string;
    kind: string | null;
    day: number | null;
    date: string | null;
    at: number;
    called: number;
  }[];
}

export type TargetView = "open" | "callback" | "done" | "all";
export type TargetSort = "name" | "step" | "tries" | "last_touch";

export interface MissionTargetRow {
  run_id: string;
  tenant_id: string;
  source: string;
  name: string | null;
  phone: string | null;
  step: number | null;
  steps: number;
  state: "open" | "callback" | "finished" | "stopped";
  tries: number;
  next_at: string | null;
  last: { at: string | null; reach: string | null; outcome: string | null; user_id: string | null } | null;
  claimed_by: string | null;
}

export interface MissionTargetsPage {
  total: number;
  offset: number;
  items: MissionTargetRow[];
  /** User id to display name, for the page's CSRs. */
  people: Record<string, string | null>;
}

export interface MissionBoard {
  estimate: boolean;
  pace: number;
  /** The month the pace is from (yyyy-MM-01). */
  basis: string | null;
  month: string;
  today: string;
  /** This month's weekdays: service jobs on the board, the part the mission booked, open against the pace, and the
   * customers who met the goal that day (their first goal-counted call; optional during deploys). */
  days: { date: string; jobs: number; by_mission: number; open: number; goal?: number }[];
  open_left: number;
  booked_by_mission: number;
  /** Customers who met the goal before this month, and in all (the Goal tile's count). Optional during deploys. */
  goal_before?: number;
  goal_met?: number;
  today_calls: { calls: number; reached: number };
  /** `goal`: outcomes the mission's goal counts; `booked`: booked jobs and surveys, whatever the goal. */
  csrs: {
    user_id: string;
    name: string | null;
    calls: number;
    reached: number;
    goal: number;
    booked: number;
    callbacks: number;
    /** What the CSR is doing now, as far as the mission knows: on a customer (a live claim), else their last call
     * today; null with neither. Optional during deploys. */
    now?: { state: "calling"; customer: string | null } | { state: "last_call"; at: string } | null;
  }[];
  outcomes: Record<string, number>;
  /** The outcomes that count as booked (`call_mission_goal_outcomes`). Optional during deploys. */
  booked_outcomes?: string[];
}

export interface ServiceTitanOptions {
  families: string[];
  campaigns: { family: string; id: string; name: string }[];
  business_units: { family: string; id: string; name: string }[];
  job_types: { family: string; id: string; name: string; business_unit_ids: string[] }[];
}

export const TARGETS_PAGE_SIZE = 50;

export function useMissionSteps(dripId: string | undefined) {
  return useQuery<MissionSteps>({
    queryKey: [...MISSIONS_KEY, "steps", dripId],
    queryFn: () => fetchApi(`/call-missions/${dripId}/steps`),
    enabled: !!dripId,
    staleTime: 30_000,
  });
}

export function useMissionTargets(dripId: string | undefined, view: TargetView, sort: TargetSort, dir: "asc" | "desc") {
  return useInfiniteQuery<MissionTargetsPage>({
    queryKey: [...MISSIONS_KEY, "targets", dripId, view, sort, dir],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchApi(
        `/call-missions/${dripId}/targets?${new URLSearchParams({
          view,
          sort_by: sort,
          sort_dir: dir,
          offset: String(pageParam),
          limit: String(TARGETS_PAGE_SIZE),
        })}`,
      ),
    getNextPageParam: (last) => {
      const next = last.offset + last.items.length;
      return next < last.total ? next : undefined;
    },
    enabled: !!dripId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useMissionBoard(dripId: string | undefined, enabled = true) {
  return useQuery<MissionBoard>({
    queryKey: [...MISSIONS_KEY, "board", dripId],
    queryFn: () => fetchApi(`/call-missions/${dripId}/board`),
    enabled: !!dripId && enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useServiceTitanOptions(tenantIds: string[]) {
  const key = [...tenantIds].sort();
  return useQuery<ServiceTitanOptions>({
    queryKey: [...MISSIONS_KEY, "st-options", key],
    queryFn: () => fetchApi(`/call-missions/servicetitan-options?tenant_ids=${encodeURIComponent(key.join(","))}`),
    enabled: key.length > 0,
    staleTime: 10 * 60_000,
  });
}

export interface TextPreview {
  /** The text as this customer gets it; null when it can't go. */
  body: string | null;
  /** Why the text can't go, in the words a save refuses it with. */
  problem: string | null;
}

/** A new mission's Text step as one customer (`name`, a sample from the list) gets it: the send's own renderer. */
export function useTextPreview(words: string, name: string | null) {
  return useQuery<TextPreview>({
    queryKey: [...MISSIONS_KEY, "text-preview", words, name],
    queryFn: () => fetchApi("/call-missions/text-preview", { method: "POST", body: JSON.stringify({ words, name }) }),
    enabled: words.trim().length > 0,
    staleTime: Infinity,
  });
}

/** Take in the audience's rows not in the mission yet, after Refine List added to it; nobody leaves. */
export function joinMission(dripId: string): Promise<{ drip_id: string; mission_join: { added: number } }> {
  return fetchApi(`/call-missions/${dripId}/join`, { method: "POST" });
}

/** A manager joins the mission's own pool, to call it themselves. Reversible with `leaveMissionPool`. */
export function joinMissionPool(dripId: string): Promise<{ drip_id: string; in_pool: boolean }> {
  return fetchApi(`/call-missions/${dripId}/pool/join`, { method: "POST" });
}

/**
 * The manager's whole new caller pool, through the same `PATCH` the mission's goal and name change through.
 * Anyone taken out hands back the customer they hold now (`release_user`); a picked CSR who holds none of the
 * mission's brands comes back as a 422.
 */
export function changeMissionCallers(
  dripId: string,
  userIds: string[],
): Promise<MissionSummary & { added: number; removed: number; released: number }> {
  return fetchApi(`/call-missions/${dripId}`, { method: "PATCH", body: JSON.stringify({ user_ids: userIds }) });
}

/** A manager leaves the mission's pool; any customer they hold releases back to the queue. */
export function leaveMissionPool(dripId: string): Promise<{ drip_id: string; in_pool: boolean; released: number }> {
  return fetchApi(`/call-missions/${dripId}/pool/leave`, { method: "POST" });
}

/** A manager turns a mission open to the team on or off: open, a CSR who holds one of its brands may join its
 *  pool themselves from All Missions, beyond who the manager assigned. */
export function setMissionOpenToTeam(dripId: string, openToTeam: boolean): Promise<MissionSummary> {
  return fetchApi(`/call-missions/${dripId}`, { method: "PATCH", body: JSON.stringify({ open_to_team: openToTeam }) });
}
